// 버킷 설정
export const BUCKETS = {
  CONTENTS: 'clozet-contents',     // 영상, 썸네일, 이미지컨텐츠
  PRODUCTS: 'clozet-products',     // 상품이미지, 샵이미지
  SUPPORT: 'clozet-support'        // 취소/교환/환불, 임시파일
} as const;

// 커스텀 도메인 설정
export const DOMAINS = {
  CONTENTS: 'https://contents.clozet.my',
  PRODUCTS: 'https://products.clozet.my',
  SUPPORT: 'https://support.clozet.my'
} as const;

// Presigned URL 생성 (Edge Function 사용)
async function getPresignedUrl(fileName: string, bucket: string, contentType: string): Promise<string> {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/generate-presigned-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({ fileName, bucket, contentType }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Presigned URL 생성 실패:', response.status, response.statusText, errorText);
    throw new Error(`Presigned URL 생성 실패: ${response.status} ${response.statusText}`);
  }

  const { url } = await response.json();
  return url;
}

// 기본 R2 업로드 함수 (Presigned URL 방식)
export const uploadToR2 = async (file: File, fileName: string, bucketName: string = BUCKETS.CONTENTS) => {
  console.log(`📤 R2 업로드 시작: bucket=${bucketName}, fileName=${fileName}, size=${file.size}`);

  try {
    // 1. Presigned URL 생성
    const presignedUrl = await getPresignedUrl(fileName, bucketName, file.type);
    console.log('✅ Presigned URL 생성 완료');

    // 2. Presigned URL로 직접 업로드
    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'Cache-Control': 'public, max-age=604800',
      },
      body: file,
    });

    console.log('업로드 응답 상태:', uploadResponse.status, uploadResponse.statusText);

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('R2 업로드 실패:', uploadResponse.status, uploadResponse.statusText);
      console.error('오류 내용:', errorText);
      throw new Error(`R2 업로드 실패: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    console.log('✅ 업로드 성공!');

    // 3. 커스텀 도메인 URL 반환 (읽기용)
    const customDomain = getDomainByBucket(bucketName);
    const publicUrl = `${customDomain}/${fileName}`;

    console.log('📍 공개 URL:', publicUrl);

    return publicUrl;
  } catch (error) {
    console.error('❌ R2 업로드 중 오류:', error);
    throw error;
  }
};

// 버킷별 도메인 반환
function getDomainByBucket(bucketName: string): string {
  switch (bucketName) {
    case BUCKETS.CONTENTS: return DOMAINS.CONTENTS;
    case BUCKETS.PRODUCTS: return DOMAINS.PRODUCTS;
    case BUCKETS.SUPPORT: return DOMAINS.SUPPORT;
    default: return DOMAINS.CONTENTS;
  }
}

// === 편의 함수들 ===

// 영상 업로드 (clozet-contents)
export const uploadVideo = async (file: File, contentId: string) => {
  const fileName = `videos/${contentId}/video.${file.name.split('.').pop()}`;
  return await uploadToR2(file, fileName, BUCKETS.CONTENTS);
};

// 썸네일 업로드 (clozet-contents)
export const uploadThumbnail = async (file: File, contentId: string, version?: string) => {
  const ext = file.name.split('.').pop();
  const suffix = version ? `_${version}` : '';
  const fileName = `thumbs/${contentId}/thumb${suffix}.${ext}`;
  return await uploadToR2(file, fileName, BUCKETS.CONTENTS);
};

// 이미지 컨텐츠 업로드 (clozet-contents)
export const uploadImageContent = async (file: File, contentId: string, index: number) => {
  const fileName = `images/${contentId}/${index}.${file.name.split('.').pop()}`;
  return await uploadToR2(file, fileName, BUCKETS.CONTENTS);
};

// 상품 대표이미지 업로드 (clozet-products)
export const uploadProductMainImage = async (file: File, productId: string, index: number) => {
  const timestamp = Date.now();
  const fileName = `items/${productId}/main/${index}_${timestamp}.${file.name.split('.').pop()}`;
  return await uploadToR2(file, fileName, BUCKETS.PRODUCTS);
};

// 상품 상세이미지 업로드 (clozet-products)
export const uploadProductDetailImage = async (file: File, productId: string, index: number) => {
  const timestamp = Date.now();
  const fileName = `items/${productId}/details/${index}_${timestamp}.${file.name.split('.').pop()}`;
  return await uploadToR2(file, fileName, BUCKETS.PRODUCTS);
};

// 샵 이미지 업로드 (clozet-products)
export const uploadShopImage = async (file: File, shopId: string, zone: number, index: number) => {
  const timestamp = Date.now();
  const fileName = `shops/${shopId}/zone${zone}/${index}_${timestamp}.${file.name.split('.').pop()}`;
  return await uploadToR2(file, fileName, BUCKETS.PRODUCTS);
};

// 취소/교환/환불 이미지 업로드 (clozet-support)
export const uploadReturnImage = async (file: File, orderId: string, index: number) => {
  const fileName = `returns/${orderId}/${index}.${file.name.split('.').pop()}`;
  return await uploadToR2(file, fileName, BUCKETS.SUPPORT);
};

// === R2 삭제 함수 ===

// URL에서 파일 경로 추출
function extractFilePathFromUrl(url: string): { bucket: string; filePath: string } | null {
  try {
    const urlObj = new URL(url);

    // 도메인별 버킷 매핑
    let bucket = '';
    if (urlObj.hostname.includes('contents.clozet.my')) {
      bucket = BUCKETS.CONTENTS;
    } else if (urlObj.hostname.includes('products.clozet.my')) {
      bucket = BUCKETS.PRODUCTS;
    } else if (urlObj.hostname.includes('support.clozet.my')) {
      bucket = BUCKETS.SUPPORT;
    } else {
      console.warn('알 수 없는 도메인:', urlObj.hostname);
      return null;
    }

    // pathname에서 / 제거
    const filePath = urlObj.pathname.startsWith('/') ? urlObj.pathname.slice(1) : urlObj.pathname;

    return { bucket, filePath };
  } catch (error) {
    console.error('URL 파싱 실패:', error);
    return null;
  }
}

// 여러 파일 일괄 삭제 (Edge Function 사용)
export const deleteMultipleFromR2 = async (fileUrls: string[]) => {
  if (fileUrls.length === 0) {
    console.log('삭제할 파일이 없습니다.');
    return { total: 0, success: 0 };
  }

  console.log(`🗑️ ${fileUrls.length}개 파일 삭제 시작 (Edge Function 사용)`);

  try {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Missing Supabase environment variables');
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/delete-r2-files`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ fileUrls }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Edge Function 오류:', response.status, response.statusText, errorText);
      throw new Error(`Edge Function 실패: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log(`✅ ${result.success}/${result.total}개 파일 삭제 완료`);

    return { total: result.total, success: result.success };
  } catch (error) {
    console.error('❌ R2 삭제 중 오류:', error);
    return { total: fileUrls.length, success: 0 };
  }
};
